import React, { useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/ethers';
import { shortenAddress } from '../../utils/format';
import { Spinner } from '../common/Loader';

export default function BlacklistPanel() {
  const { read, write } = useContracts();
  const notify = useNotify();

  const [lookup, setLookup] = useState('');
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  async function check() {
    if (!lookup) return notify.warning('Enter a user ID.');
    setBusy(true);
    try {
      const user = await read.storage.getUser(lookup);
      if (!user.exists) {
        notify.error('User not found.');
        setInfo(null);
      } else {
        const black = await read.storage.blacklisted(lookup);
        setInfo({ userId: lookup, wallet: user.wallet, isActive: user.isActive, blacklisted: black });
      }
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(status) {
    if (!info) return;
    if (!write) return notify.error('Wallet not connected.');
    setBusy(true);
    try {
      const tx = await write.storage.setBlacklist(info.userId, status);
      notify.info('Updating blacklist...');
      await tx.wait();
      notify.success(status ? 'User blacklisted.' : 'User un-blacklisted.');
      setInfo({ ...info, blacklisted: status });
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-panel">
      <h3>Blacklist Management</h3>
      <p className="panel-hint">Block/unblock a user by ID. Blacklisted users cannot earn or withdraw.</p>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">User ID</label>
            <input className="input" type="text" placeholder="e.g. 5000"
              value={lookup} onChange={(e) => setLookup(e.target.value.replace(/\D/g, ''))} />
          </div>
        </div>
        <button className="btn btn-secondary" onClick={check} disabled={busy || !lookup}>
          {busy && !info ? <Spinner /> : 'Look Up'}
        </button>
      </div>

      {info && (
        <div style={{ padding: 20, background: 'var(--bg-tertiary)', borderRadius: 12, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>User</div>
              <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-mono)' }}>
                ID {info.userId} · {shortenAddress(info.wallet)}
              </div>
            </div>
            <span className={`badge ${info.blacklisted ? 'badge-error' : (info.isActive ? 'badge-success' : 'badge-muted')}`}>
              {info.blacklisted ? 'Blacklisted' : (info.isActive ? 'Active' : 'Inactive')}
            </span>
          </div>
          {info.blacklisted ? (
            <button className="btn btn-primary btn-block" disabled={busy} onClick={() => toggle(false)}>
              {busy ? <Spinner /> : 'Remove from Blacklist'}
            </button>
          ) : (
            <button className="btn btn-danger btn-block" disabled={busy} onClick={() => toggle(true)}>
              {busy ? <Spinner /> : 'Blacklist This User'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
